import React from "react";
import Image from "next/image";
import SearchIcon from "@/assets/icons/search.svg";
import {
  BenefitsContent,
  NoResultsPageContent,
  PlpContent,
  SearchSection,
  ISeoFooterText,
  TranslationData,
} from "@/common/lib/types";
import BenefitSection from "../home/benefitSection/BenefitSection";
import HeaderVariants from "../common/headerVariants/HeaderVariants";
import Link from "next/link";
import StoreLocatorIcon from "@/assets/logos/store-locator-doctorm.png";
import { fontLTBukra } from "@/utils/fonts";
import { cn } from "@/lib/tailwind";
import SeoFooterText from "../common/SeoFooterText";

interface NoResultsProps {
  data?: SearchSection;
  title?: string;
  searchParam?: string;
  benefits?: BenefitsContent;
  result?: NoResultsPageContent;
  desc?: string;
  translations: TranslationData;
  blockdata: PlpContent;
  seoFooterText?: ISeoFooterText;
}

const NoResults = ({
  data,
  title,
  searchParam,
  benefits,
  result,
  desc,
  translations,
  blockdata,
  seoFooterText,
}: NoResultsProps) => {
  const noResultData =
    searchParam && data?.noResultSearchOverlay
      ? data?.noResultSearchOverlay
      : data?.noResultSearchPage;

  if (!data) return <></>;

  const noResultPlpData = result?.content;
  const noResultBlockData = blockdata?.pageData?.noresults;

  const emptySearchData = {
    description: noResultPlpData?.storeLocatorSection?.description,
    buttonText: noResultPlpData?.storeLocatorSection?.ctaTitle,
    noResultDesc: noResultBlockData?.noResultDescription || noResultPlpData?.noResultDescription,
    editTiles: noResultBlockData?.editSection?.edits || noResultPlpData?.editSection?.edits,
    isLocatorEnabled:
      noResultBlockData && "enableStoreLocator" in noResultBlockData
        ? noResultBlockData.enableStoreLocator
        : noResultPlpData?.enableStoreLocator,
    editType: noResultBlockData?.editSection?.type || noResultPlpData?.editSection?.type,
    backgroundType:
      noResultBlockData?.editSection?.background?.type ||
      noResultPlpData?.editSection?.background?.type,
  };

  return (
    <>
      <section className="mt-20">
        {title && (
          <div className="flex flex-col items-center gap-5 px-[20px] md:px-[144px]">
            <h1 className="text-[22px] font-bold xl:text-[32px] xl:font-medium">{title}</h1>
            {desc && (
              <HeaderVariants
                variant="withSubheading"
                subheading={desc}
                translations={translations}
                withBackground={false}
              />
            )}
          </div>
        )}
        {!searchParam && emptySearchData?.noResultDesc ? (
          <>
            {emptySearchData.isLocatorEnabled && (
              <div className="mb-[8px] mt-10 flex w-full flex-col items-center px-5">
                <div className="h-auto w-[47px] md:w-[59px]">
                  <Image
                    src={StoreLocatorIcon}
                    alt="storelocator logo"
                    sizes="(max-width: 768px) 47px, 59px"
                    className="object-contain"
                  />
                </div>
                <h2
                  className={cn(
                    "w-[82%] px-0 pb-[32px] pt-[25px] text-center text-[16px] font-bold uppercase tracking-negative md:w-[45%] md:pb-[40px] md:pt-[33px] md:text-[18px]",
                  )}
                >
                  {emptySearchData.description}
                </h2>
                <Link href="/find-store">
                  <button
                    type="button"
                    className="flex h-[33px] w-[170px] items-center justify-center rounded-none border-0 border-black bg-black p-0 text-[13px] font-bold text-white opacity-100 outline-none transition-all ease-in-out focus-visible:rounded-none focus-visible:border-2 focus-visible:border-black focus-visible:bg-transparent focus-visible:text-black active:rounded-none active:bg-black active:text-white active:opacity-100 sm:px-10 sm:py-4 md:p-0"
                    style={{ fontFamily: "Public Sans" }}
                  >
                    {emptySearchData.buttonText}
                  </button>
                </Link>
              </div>
            )}
            <div className="flex justify-center pb-5 pt-[60px] md:pt-[52px]">
              <p
                className="w-[60%] max-w-[800px] text-center text-[14px] tracking-negative text-tertiary-6 md:text-[16px] xl:w-[40%]"
                style={{ fontFamily: "Public Sans" }}
              >
                {emptySearchData.noResultDesc}
              </p>
            </div>
            <HeaderVariants
              variant="withIcons"
              fontFamily="Public Sans"
              background={emptySearchData.backgroundType}
              editType={emptySearchData.editType}
              icons={(emptySearchData.editTiles || [])?.map((item: any) => ({
                alt: item?.title || "",
                label: item?.title || "",
                src: item?.image || "",
                url: item?.link?.url || "",
              }))}
            />
          </>
        ) : (
          <>
            <div className="mb-2 flex w-full flex-col items-center px-4 py-5">
              <Image src={SearchIcon} height={64} width={64} alt="search icon" />
              <h4 className="mb-1 mt-6 text-center text-2xl font-bold">
                {noResultData?.title?.replace(
                  "{{searchTerm}}",
                  searchParam || title || blockdata?.pageData?.name || "",
                )}
              </h4>
              <p className="text-center text-sm text-tertiary-6">{noResultData?.subTitle || ""}</p>
            </div>
            <HeaderVariants
              variant="withIcons"
              icons={(noResultData?.edits || [])?.map((item) => ({
                alt: item?.title || "",
                label: item?.title || "",
                src: item?.image || "",
                url: item?.link?.url || "",
              }))}
              background="colorStrip"
            />
          </>
        )}
      </section>

      {seoFooterText && (
        <section className="my-9">
          <SeoFooterText
            title={seoFooterText.title}
            description={seoFooterText.description}
            accordionContent={seoFooterText?.collapseSection}
            translations={translations}
          />
        </section>
      )}

      {benefits && (
        <section className="mt-20">
          <BenefitSection benefitsData={benefits} />
        </section>
      )}
    </>
  );
};

export default NoResults;
